'use client';

/**
 * SignerPolicyBuilder (Issue #1307)
 *
 * Admin editor for a term sheet template's `SignerPolicy`. Each row declares
 * one required signing role, its cardinality (one / many / optional), the
 * party-resolver source, and the 1-based signing order. The component
 * enforces the invariants from `SignerPolicySchema` + the issue body:
 *
 *   • At least one borrower / authorized_officer row required for any
 *     non-empty policy (so the envelope actually has a consumer signer).
 *   • Counter-sign rows must use `lender_signer` or `credit_manager` and
 *     have a higher signing order than any non-counter-sign row.
 *   • Rows with cardinality 'one' or 'many' MUST declare a source.
 *   • Duplicate signing orders are rejected in the MVP (parallel signing
 *     is a future feature).
 *
 * The component reports validity to the parent via `onValidityChange` so
 * the editor can disable save when the policy is malformed.
 *
 * TODO(Phase 5 / #1531): migrate from admin.templates.termsheets.* to
 * termsheets.editor.* keys (the new namespace was added in Phase 0 Wave 1).
 */

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { type SignerPolicy, type SignerPolicyItem, SignerPolicySchema, SignerRole } from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  GlassAlert,
  GlassButton,
  GlassCheckbox,
  GlassChip,
  GlassIconButton,
  GlassSelect,
  GlassTextField,
  Stack,
  Typography,
} from '../components';
import { useI18n } from '../hooks';

// ============================================================================
// Constants / options
// ============================================================================

type SignerRoleValue = (typeof SignerRole)[keyof typeof SignerRole];

const ROLE_VALUES: readonly SignerRoleValue[] = [
  SignerRole.BORROWER,
  SignerRole.CO_BORROWER,
  SignerRole.GUARANTOR,
  SignerRole.AUTHORIZED_OFFICER,
  SignerRole.LENDER_SIGNER,
  SignerRole.CREDIT_MANAGER,
  SignerRole.WITNESS,
  SignerRole.NOTARY,
  SignerRole.CC,
] as const;

const COUNTER_SIGN_ALLOWED: readonly SignerRoleValue[] = [SignerRole.LENDER_SIGNER, SignerRole.CREDIT_MANAGER] as const;

const CONSUMER_SIGNER_ROLES = new Set<SignerRoleValue>([
  SignerRole.BORROWER,
  SignerRole.CO_BORROWER,
  SignerRole.AUTHORIZED_OFFICER,
  SignerRole.GUARANTOR,
]);

const CARDINALITY_VALUES = ['one', 'many', 'optional'] as const;
type Cardinality = (typeof CARDINALITY_VALUES)[number];

const SOURCE_VALUES = [
  'case.assignedTo',
  'case.creditManager',
  'case.borrowers',
  'case.guarantors',
  'case.entitySignatory',
  'manual',
] as const;
type SourceValue = (typeof SOURCE_VALUES)[number];

function roleKey(role: string): string {
  return `admin.templates.termsheets.signerPolicy.role.${role}`;
}
function cardinalityKey(c: string): string {
  return `admin.templates.termsheets.signerPolicy.cardinality.${c}`;
}
function sourceKey(s: string): string {
  return `admin.templates.termsheets.signerPolicy.source.${s}`;
}

// ============================================================================
// Local validation — matches SignerPolicySchema + issue acceptance criteria
// ============================================================================

/**
 * Result of running `validateSignerPolicy` against an admin-authored policy.
 * Exported so unit tests (and future callers that need to show an aggregated
 * error count without rendering the component) can use the same validator.
 */
export interface SignerPolicyValidationState {
  valid: boolean;
  /** i18n keys (not raw strings) so callers can render localized errors. */
  errorKeys: string[];
}

/**
 * Validate a SignerPolicy against the backend SignerPolicySchema invariants
 * PLUS the MVP-specific rules from the issue body:
 *   • At least one consumer signer (borrower / co_borrower / guarantor /
 *     authorized_officer) is required when the policy is non-empty.
 *   • Counter-sign rows must use lender_signer or credit_manager.
 *   • Counter-sign rows must have signingOrder > all non-counter-sign rows.
 *   • Rows with cardinality 'one' or 'many' must declare a source.
 *   • Signing orders must be positive integers; duplicates are rejected for
 *     MVP (parallel signing is a future feature).
 *
 * The function is pure and returns i18n keys (not localized strings) so that
 * both the component and unit tests can consume it.
 */
export function validateSignerPolicy(policy: SignerPolicy): SignerPolicyValidationState {
  const errors: string[] = [];

  // Empty policy is valid (tenant chose "no signers required").
  if (policy.length === 0) {
    return { valid: true, errorKeys: [] };
  }

  // Must include at least one borrower or authorized_officer.
  const hasConsumerSigner = policy.some((item) => CONSUMER_SIGNER_ROLES.has(item.role as SignerRoleValue));
  if (!hasConsumerSigner) {
    errors.push('admin.templates.termsheets.signerPolicy.errors.missingBorrower');
  }

  // Signing orders: positive ints, unique, counter-sign must be >= all non-counter-sign.
  const seenOrders = new Set<number>();
  let hasDuplicate = false;
  let hasInvalidOrder = false;
  let maxNonCounter = 0;
  let minCounter = Number.POSITIVE_INFINITY;
  let counterSignRoleInvalid = false;

  for (const item of policy) {
    if (!Number.isInteger(item.signingOrder) || item.signingOrder < 1) {
      hasInvalidOrder = true;
    }
    if (seenOrders.has(item.signingOrder)) {
      hasDuplicate = true;
    }
    seenOrders.add(item.signingOrder);

    if (item.counterSign) {
      if (!COUNTER_SIGN_ALLOWED.includes(item.role as SignerRoleValue)) {
        counterSignRoleInvalid = true;
      }
      if (item.signingOrder < minCounter) minCounter = item.signingOrder;
    } else {
      if (item.signingOrder > maxNonCounter) maxNonCounter = item.signingOrder;
    }

    // 'one' / 'many' require a source
    if ((item.cardinality === 'one' || item.cardinality === 'many') && !item.source) {
      errors.push('admin.templates.termsheets.signerPolicy.errors.missingSource');
    }
  }

  if (hasInvalidOrder) errors.push('admin.templates.termsheets.signerPolicy.errors.invalidOrder');
  if (hasDuplicate) errors.push('admin.templates.termsheets.signerPolicy.errors.duplicateOrder');
  if (counterSignRoleInvalid) errors.push('admin.templates.termsheets.signerPolicy.errors.counterSignRole');
  if (minCounter !== Number.POSITIVE_INFINITY && minCounter <= maxNonCounter) {
    errors.push('admin.templates.termsheets.signerPolicy.errors.counterSignOrder');
  }

  // Backend schema's final say: if anything was missed above, Zod should catch it.
  const parsed = SignerPolicySchema.safeParse(policy);
  if (!parsed.success) {
    // Avoid leaking raw Zod messages; the specific checks above cover UI signalling.
    // If we end up here with no error keys (unexpected), add the generic save-failed key.
    if (errors.length === 0) {
      errors.push('admin.templates.termsheets.errors.saveFailed');
    }
  }

  // De-duplicate error keys so we don't render the same message twice.
  return { valid: errors.length === 0, errorKeys: Array.from(new Set(errors)) };
}

// ============================================================================
// Component
// ============================================================================

export interface SignerPolicyBuilderProps {
  value: SignerPolicy;
  onChange: (next: SignerPolicy) => void;
  onValidityChange?: (valid: boolean) => void;
  disabled?: boolean;
}

export function SignerPolicyBuilder({ value, onChange, onValidityChange, disabled = false }: SignerPolicyBuilderProps) {
  const { t } = useI18n();

  const validation = useMemo(() => validateSignerPolicy(value), [value]);

  useEffect(() => {
    onValidityChange?.(validation.valid);
  }, [validation.valid, onValidityChange]);

  const updateRow = useCallback(
    (index: number, patch: Partial<SignerPolicyItem>) => {
      const next = value.map((row, i) => (i === index ? ({ ...row, ...patch } as SignerPolicyItem) : row));
      onChange(next);
    },
    [value, onChange],
  );

  const addRow = useCallback(() => {
    const nextOrder = value.length > 0 ? Math.max(...value.map((r) => r.signingOrder)) + 1 : 1;
    const newRow: SignerPolicyItem = {
      role: SignerRole.BORROWER,
      cardinality: 'one',
      signingOrder: nextOrder,
      counterSign: false,
      source: 'case.borrowers',
    };
    onChange([...value, newRow]);
  }, [value, onChange]);

  const removeRow = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  // ----- Summary chip -----
  const summaryLabel =
    value.length === 0
      ? t('admin.templates.termsheets.signerPolicy.summary.empty')
      : value.length === 1
        ? t('admin.templates.termsheets.signerPolicy.summary.single', { count: 1 })
        : t('admin.templates.termsheets.signerPolicy.summary.multiple', { count: value.length });

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('admin.templates.termsheets.signerPolicy.title')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t('admin.templates.termsheets.signerPolicy.description')}
        </Typography>
      </Box>

      <Box>
        <GlassChip label={summaryLabel} size="small" variant="outlined" />
      </Box>

      {/* Validation errors */}
      {validation.errorKeys.length > 0 ? (
        <Stack spacing={1}>
          {validation.errorKeys.map((key) => (
            <GlassAlert key={key} visible severity="error">
              {t(key)}
            </GlassAlert>
          ))}
        </Stack>
      ) : null}

      {/* Rows */}
      <Stack spacing={1.5}>
        {value.map((row, index) => {
          const roleOptions = ROLE_VALUES.map((r) => ({ value: r, label: t(roleKey(r)) }));
          const cardinalityOptions = CARDINALITY_VALUES.map((c) => ({
            value: c,
            label: t(cardinalityKey(c)),
          }));
          const sourceOptions = SOURCE_VALUES.map((s) => ({
            value: s,
            label: t(sourceKey(s)),
          }));

          return (
            <Box
              key={`policy-row-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: '1.5fr 1fr 1.5fr 0.7fr 0.8fr 0.4fr',
                },
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <GlassSelect
                label={t('admin.templates.termsheets.signerPolicy.column.role')}
                value={row.role}
                onChange={(e) => updateRow(index, { role: String(e.target.value) as SignerRoleValue })}
                options={roleOptions}
                disabled={disabled}
                size="small"
              />
              <GlassSelect
                label={t('admin.templates.termsheets.signerPolicy.column.cardinality')}
                value={row.cardinality}
                onChange={(e) => updateRow(index, { cardinality: String(e.target.value) as Cardinality })}
                options={cardinalityOptions}
                disabled={disabled}
                size="small"
              />
              <GlassSelect
                label={t('admin.templates.termsheets.signerPolicy.column.source')}
                value={row.source ?? ''}
                onChange={(e) =>
                  updateRow(index, {
                    source: (e.target.value ? String(e.target.value) : undefined) as SourceValue | undefined,
                  })
                }
                options={sourceOptions}
                disabled={disabled}
                size="small"
              />
              <GlassTextField
                label={t('admin.templates.termsheets.signerPolicy.column.signingOrder')}
                type="number"
                value={String(row.signingOrder)}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  // Clamp to 1-20 per SignerPolicyItemSchema
                  const bounded = Number.isNaN(parsed) ? 1 : Math.min(20, Math.max(1, parsed));
                  updateRow(index, { signingOrder: bounded });
                }}
                disabled={disabled}
                size="small"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <GlassCheckbox
                  checked={row.counterSign}
                  onChange={(e) => updateRow(index, { counterSign: e.target.checked })}
                  disabled={disabled}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('admin.templates.termsheets.signerPolicy.column.counterSign')}
                </Typography>
              </Box>
              <GlassIconButton
                size="small"
                onClick={() => removeRow(index)}
                disabled={disabled}
                aria-label={t('admin.templates.termsheets.signerPolicy.column.remove')}
              >
                <DeleteIcon fontSize="small" />
              </GlassIconButton>
            </Box>
          );
        })}
      </Stack>

      <Box>
        <GlassButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRow}
          disabled={disabled || value.length >= 20}
          size="small"
        >
          {t('admin.templates.termsheets.signerPolicy.addRow')}
        </GlassButton>
      </Box>
    </Stack>
  );
}
